import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { ActionItem, ActionItemStatus, AsyncState } from '@/types';
import * as actionItemService from '@/services/actionItemService';
import { createNewsletterWithCarryForward } from '@/store/slices/newslettersSlice';

interface ActionItemsState extends AsyncState {
  items: ActionItem[];
}

const initialState: ActionItemsState = {
  items: [],
  status: 'idle',
  error: null,
};

export const fetchActionItems = createAsyncThunk('actionItems/fetchAll', async () => {
  return actionItemService.listActionItems();
});

export const createActionItemThunk = createAsyncThunk(
  'actionItems/create',
  async (input: actionItemService.CreateActionItemInput) => {
    return actionItemService.createActionItem(input);
  },
);

export const updateActionItemThunk = createAsyncThunk('actionItems/update', async (item: ActionItem) => {
  return actionItemService.updateActionItem(item);
});

export const setActionItemStatusThunk = createAsyncThunk(
  'actionItems/setStatus',
  async ({ item, status }: { item: ActionItem; status: ActionItemStatus }) => {
    return actionItemService.setActionItemStatus(item, status);
  },
);

export const deleteActionItemThunk = createAsyncThunk('actionItems/delete', async (id: string) => {
  await actionItemService.deleteActionItem(id);
  return id;
});

const actionItemsSlice = createSlice({
  name: 'actionItems',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchActionItems.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchActionItems.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload;
      })
      .addCase(fetchActionItems.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? 'Failed to load action items';
      })
      .addCase(createActionItemThunk.fulfilled, (state, action) => {
        state.items.unshift(action.payload);
      })
      .addCase(updateActionItemThunk.fulfilled, (state, action) => {
        const index = state.items.findIndex((i) => i.id === action.payload.id);
        if (index !== -1) state.items[index] = action.payload;
      })
      .addCase(setActionItemStatusThunk.fulfilled, (state, action) => {
        const index = state.items.findIndex((i) => i.id === action.payload.id);
        if (index !== -1) state.items[index] = action.payload;
      })
      .addCase(deleteActionItemThunk.fulfilled, (state, action) => {
        state.items = state.items.filter((i) => i.id !== action.payload);
      })
      .addCase(createNewsletterWithCarryForward.fulfilled, (state, action) => {
        state.items.unshift(...action.payload.carriedItems);
      });
  },
});

export default actionItemsSlice.reducer;
